// externals
import type { Reducer } from 'redux';

// libraries
import { initActionTypes } from '@makemydeal/dr-shared-store';

// actions
import * as Actions from '@makemydeal/dr-dash-store/actionTypes/openDealActionTypes';
import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';

// interfaces
import type { IOpenDeal } from '../types/IOpenDeal';
import { mapToDxgVehicle } from '../mappers/mapToDxgVehicle';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';
import { initialState } from './initialState';

export type OpenDealReducer = Reducer<IOpenDeal>;

export const reducer: Reducer<IOpenDeal> = (state: IOpenDeal = initialState, action) => {
    const { payload, type } = action;

    switch (type) {
        case initActionTypes.INIT_PENCIL_SUCCESS:
        case initActionTypes.INIT_PENCIL_INVALID_DEAL: {
            const newState = {
                ...state,
                commonOrgId: payload.offer?.incompleteDealDetails?.commonOrgId || '',
                isDealModified: false,
                selectedDealer: payload.offer?.incompleteDealDetails?.missingDealership ? undefined : payload.dealer,
                selectedVehicle: mapToDxgVehicle(payload.vehicle),
                selectedConsumer: payload.offer?.shopper,
                isDealSaveInProgress: false
            };

            return {
                ...newState,
                stateSnapshot: {
                    commonOrgId: newState.commonOrgId,
                    isDealModified: newState.isDealModified,
                    selectedDealer: newState.selectedDealer,
                    selectedVehicle: newState.selectedVehicle,
                    selectedConsumer: newState.selectedConsumer,
                    isDealSaveInProgress: newState.isDealSaveInProgress
                }
            };
        }

        /**
         * Specifically, it resets the deal save status to Idle, which stops the loader on the open deal activity page
         * and ensures the updated deal banner is hidden during the redirection process for a smoother user experience.
         */

        case Actions.FETCH_DEAL_HISTORY_SUCCESS: {
            return {
                ...state,
                saveDealStatus: ApiRequestStatus.Idle
            };
        }
        case Actions.DEALERSHIP_UPDATE: {
            return {
                ...state,
                selectedDealer: payload,
                isDealModified: true,
                commonOrgId: payload.commonOrgId || ''
            };
        }
        case Actions.OPEN_DEAL: {
            return {
                ...state,
                isOpeningDeal: true,
                isUpdateDealFailure: false
            };
        }
        case Actions.SAVE_DEAL_REQUEST:
        case offerActionTypes.OFFER_SAVE_REQUEST: {
            return {
                ...state,
                saveDealStatus: ApiRequestStatus.Loading,
                isDealSaveInProgress: true
            };
        }

        case Actions.SAVE_DEAL_FAILURE:
        case offerActionTypes.OFFER_SAVE_FAILURE: {
            return {
                ...state,
                isOpeningDeal: false,
                isUpdateDealFailure: true,
                saveDealStatus: ApiRequestStatus.Error
            };
        }
        case Actions.COMMON_CONSUMER_UPDATE: {
            return {
                ...state,
                selectedConsumer: {
                    ...payload.selectedConsumer,
                    commonConsumerId: state.selectedConsumer?.commonConsumerId
                }
            };
        }
        case Actions.COMMON_CONSUMER_UPDATE_VALIDATION_STATE: {
            return {
                ...state,
                isConsumerValid: payload.isConsumerValid,
                isDealModified: true,
                stateSnapshot: {
                    ...state.stateSnapshot,
                    isDealModified: true,
                    isConsumerValid: payload.isConsumerValid
                }
            };
        }
        case Actions.SET_ENVIRONMENT: {
            return {
                ...state,
                environment: payload.environment
            };
        }
        case Actions.SWITCH_VEHICLE: {
            return {
                ...state,
                isChangeVehicleEditView: !state.isChangeVehicleEditView,
                selectedVehicle: mapToDxgVehicle(action.payload)
            };
        }
        case Actions.SYNC_CONSUMER_DATA: {
            return {
                ...state,
                syncConsumerData: action.payload
            };
        }
        case Actions.TOGGLE_CHANGE_VEHICLE_EDIT_VIEW: {
            return {
                ...state,
                isChangeVehicleEditView: !state.isChangeVehicleEditView
            };
        }

        case Actions.RESET_OPEN_DEAL_STATE: {
            return state.stateSnapshot
                ? {
                      ...initialState,
                      ...state.stateSnapshot,
                      stateSnapshot: state.stateSnapshot
                  }
                : state;
        }
        default: {
            return state;
        }
    }
};
